import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import Editor from "components/editor/Editor";
import usePostActions from "components/forum/hooks/usePostActions";
import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";
import { FORM_ERROR } from "final-form";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import { ButtonGroup, LinkButton } from "lib/miolo";
import checkCanSubmit from "lib/utils/form/checkCanSubmit";
import restartForm from "lib/utils/form/restartForm";
import validateFormValuesAgainstSchema from "lib/utils/form/validateFormValuesAgainstSchema";
import React from "react";
import { useField, useForm } from "react-final-form-hooks";
import { object, string } from "yup";

interface IForumEditPostProps {
  onCancel: () => void;
  shortId: string;
  id: string;
  initialState: string;
  isReply?: boolean;
}

const ForumEditPost = React.forwardRef<HTMLDivElement | null, IForumEditPostProps>(
  ({ onCancel, shortId, id, initialState, isReply }, ref) => {
    const { editPost, editPostReply } = usePostActions();

    const { form, handleSubmit, submitting, ...props } = useForm<{
      bodyAsJson: string;
      bodyAsPlainText: string;
    }>({
      onSubmit: async ({ bodyAsJson, bodyAsPlainText }, form) => {
        try {
          if (isReply) {
            await editPostReply({
              variables: {
                replyId: id,
                bodyAsJson,
                bodyAsPlainText,
              },
              refetchQueries: [
                {
                  query: PostRepliesQuery,
                  variables: {
                    postShortId: shortId,
                    cursor: null,
                  },
                },
              ],
            });
          } else {
            await editPost({
              variables: {
                title: "",
                postId: id,
                bodyAsJson,
                bodyAsPlainText,
              },
              refetchQueries: [
                {
                  query: PostRepliesQuery,
                  variables: {
                    postShortId: shortId,
                    cursor: null,
                  },
                },
              ],
            });
          }

          setTimeout(() => {
            restartForm(form);
            onCancel();
          }, 1);
        } catch (err) {
          if (err instanceof Error) {
            return {
              [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
            };
          }
          return {
            [FORM_ERROR]: "Erro desconhecido",
          };
        }

        return null;
      },
      validate: validateFormValuesAgainstSchema(replyPostInputFormDataShape),
      initialValues: {
        bodyAsJson: "",
        bodyAsPlainText: "",
      },
    });

    const fieldBodyAsJson = useField("bodyAsJson", form);
    const fieldBodyAsPlainText = useField("bodyAsPlainText", form);
    const canSubmit = submitting ? false : checkCanSubmit(props);

    return (
      <div ref={ref} className="col-span-4 bg-white md:col-span-9">
        <form
          onSubmit={handleSubmit}
          css={{
            display: "grid",
            gridRowGap: 20,
          }}
        >
          <Editor
            onChange={(bodyAsJson, bodyAsPlainText) => {
              fieldBodyAsJson.input.onChange(bodyAsJson);
              fieldBodyAsPlainText.input.onChange(bodyAsPlainText);
            }}
            initialState={initialState}
          />

          <div>
            {fieldBodyAsJson.meta.touched && fieldBodyAsJson.meta.error && (
              <InlineErrorSpan>{fieldBodyAsJson.meta.error}</InlineErrorSpan>
            )}
          </div>

          <div className="flex w-full justify-end">
            <ButtonGroup stack="horizontally" spacing={20}>
              <LinkButton
                disabled={submitting}
                variant="black"
                onClick={() => {
                  restartForm(form);
                  onCancel();
                }}
              >
                Cancelar
              </LinkButton>

              <Button
                className={classNames(
                  "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                  "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                  {
                    "cursor-default bg-brand/50": !canSubmit,
                  },
                )}
                type="submit"
                disabled={!canSubmit}
              >
                {submitting ? "Aguarde..." : "Enviar"}
              </Button>
            </ButtonGroup>
          </div>
        </form>
      </div>
    );
  },
);

export default ForumEditPost;

const replyPostInputFormDataShape = object().shape({
  bodyAsJson: string()
    .trim()
    .required("O comentário é obrigatório")
    .min(3, "O comentário deve ter no mínimo 3 caracteres")
    .max(8192, "O comentário deve ter no máximo 12288 caracteres"),
  bodyAsPlainText: string()
    .trim()
    .required("O comentário é obrigatório")
    .min(3, "O comentário deve ter no mínimo 3 caracteres")
    .max(8192, "O comentário deve ter no máximo 12288 caracteres"),
});
